import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useState } from "react";
import { SearchInput } from "@altek/ui/components/data/SearchInput";
import { Banner } from "@altek/ui/components/feedback/Banner";
import { ConfirmDialog } from "@altek/ui/components/feedback/ConfirmDialog";
import { Drawer } from "@altek/ui/components/feedback/Drawer";
import { EmptyState } from "@altek/ui/components/feedback/EmptyState";
import { Modal } from "@altek/ui/components/feedback/Modal";
import { SkLine } from "@altek/ui/components/feedback/Skeleton";
import { SwitchField } from "@altek/ui/components/form/ChoiceControls";
import { SelectField } from "@altek/ui/components/form/Select";
import { TextField } from "@altek/ui/components/form/TextField";
import { Avatar } from "@altek/ui/components/ui/Avatar";
import { Badge } from "@altek/ui/components/ui/Badge";
import { Button } from "@altek/ui/components/ui/Button";
import { Menu, type MenuAction } from "@altek/ui/components/ui/Menu";
import { formatCount } from "@altek/ui/lib/format/number";
import { toast } from "@altek/ui/lib/utils/toast";
import { useAuth } from "@/app/providers/AuthProvider";
import { chatApi } from "../api/chat.api";
import { chatConversationQuery, chatPeopleQuery } from "../api/chat.queries";
import {
  CHAT_PERMISSIONS,
  type ChatConversationDetail,
  type ChatConversationMember,
  type ChatMemberRole,
  type DirectoryPersonRow,
} from "../api/chat.types";
import { chatErrorMessage } from "../lib/chat-errors";
import { invalidateAfterLeaving, invalidateChat } from "../lib/chat-invalidate";
import { conversationTitle, personPhotoUrl } from "../lib/chat-map";
import { personSubtitle } from "../lib/chat-people";

/**
 * GRUP ÜYELERİ — liste, ekle, çıkar, yeniden adlandır, sahip yap (sohbet-duzeltme 20 Ekim 2026,
 * denetim H7 — ölçüldü: grup menüsünde yalnız Sabitle · Sessize al · Arşivle · Gruptan ayrıl
 * vardı; uçlar çalışıyordu ama ekran yoktu, ve sahibi ayrılmak isteyen grup 422 `LAST_OWNER` ile
 * kilitleniyordu).
 *
 * HER ÜYE GÖRÜYOR, SAHİP YÖNETİYOR. Üye listesi grubun her üyesine açık; ekleme, çıkarma, ad ve
 * sahiplik yalnız grubun SAHİBİNE ve `chat.conversation.manage` anahtarını tutana çiziliyor
 * (sunucunun iki kuralı: rota anahtarı + `OWNER_REQUIRED`). Yapamayacağı eylem için düğme yok.
 *
 * SAHİPLİK DEVRİ = birini sahip yapmak. Bir grubun birden çok sahibi olabiliyor; sahip, başka bir
 * sahip varken kendini üyeye çevirebilir ya da ayrılabilir. Son sahip için ayrılmak ayrı bir
 * pencerede «önce birini seçin» oluyor (`HandOverDialog`).
 *
 * HOVER YOK: satır eylemleri satırın «Diğer işlemler» menüsünde (dokunma ve klavye), seçim
 * basmayla ve seçili işaretiyle belli.
 */

/** «Ayşe Örnek (siz)» — the reader's own row says so, like the people picker does. */
function displayNameOf(member: ChatConversationMember, meId: string): string {
  return member.userId === meId ? `${member.displayName} (siz)` : member.displayName;
}

export function GroupMembersDrawer({
  conversationId,
  meId,
  onClose,
  onRequestLeave,
}: {
  /** The group whose members are shown; `null` closes the drawer. */
  conversationId: string | null;
  meId: string;
  onClose: () => void;
  onRequestLeave: (conversation: ChatConversationDetail) => void;
}) {
  const queryClient = useQueryClient();
  const { permissions } = useAuth();
  const detail = useQuery(chatConversationQuery(conversationId, { enabled: conversationId !== null }));
  const conversation = detail.data;

  const canManage =
    permissions.can(CHAT_PERMISSIONS.manage) && conversation?.kind === "GROUP" && conversation.role === "OWNER";
  const owners = conversation?.allMembers.filter((member) => member.role === "OWNER") ?? [];

  /* ---------------------------------------------------------------- rename */
  const [titleDraft, setTitleDraft] = useState<{ id: string; value: string } | null>(null);
  const titleValue =
    titleDraft && conversation && titleDraft.id === conversation.id ? titleDraft.value : (conversation?.title ?? "");
  const rename = useMutation({
    mutationFn: (input: { id: string; title: string; version: number }) =>
      chatApi.rename(input.id, { title: input.title, version: input.version }),
    onSuccess: async () => {
      setTitleDraft(null);
      await invalidateChat(queryClient);
      toast.success("Grubun adı değiştirildi.");
    },
    onError: (error) => toast.errorMessage("Grubun adı değiştirilemedi", chatErrorMessage(error)),
  });
  const trimmedTitle = titleValue.trim();
  const titleChanged = conversation !== undefined && trimmedTitle !== "" && trimmedTitle !== conversation.title;

  /* ---------------------------------------------------------------- roles, removal */
  const role = useMutation({
    mutationFn: (input: { id: string; userId: string; role: ChatMemberRole }) =>
      chatApi.setMemberRole(input.id, input.userId, input.role),
    onSuccess: async (_result, input) => {
      await invalidateChat(queryClient);
      toast.success(input.role === "OWNER" ? "Grup sahibi yapıldı." : "Sahiplik geri alındı.");
    },
    onError: (error) => toast.errorMessage("Sahiplik değiştirilemedi", chatErrorMessage(error)),
  });
  const [removing, setRemoving] = useState<ChatConversationMember | null>(null);
  const remove = useMutation({
    mutationFn: (input: { id: string; userId: string }) => chatApi.removeMember(input.id, input.userId),
    onSuccess: async () => {
      setRemoving(null);
      await invalidateChat(queryClient);
      toast.success("Üye gruptan çıkarıldı.");
    },
    onError: (error) => toast.errorMessage("Üye çıkarılamadı", chatErrorMessage(error)),
  });

  const memberActions = (member: ChatConversationMember): MenuAction[] => {
    if (!canManage || !conversation) return [];
    const self = member.userId === meId;
    const items: MenuAction[] = [];
    if (member.role === "MEMBER") {
      items.push({
        label: "Sahip yap",
        onSelect: () => role.mutate({ id: conversation.id, userId: member.userId, role: "OWNER" }),
      });
    } else if (owners.length > 1) {
      // The last owner is never demoted (422 `LAST_OWNER`) — the item is absent, not disabled.
      items.push({
        label: self ? "Sahiplikten çekil" : "Sahipliği geri al",
        onSelect: () => role.mutate({ id: conversation.id, userId: member.userId, role: "MEMBER" }),
      });
    }
    if (!self) items.push({ label: "Gruptan çıkar", destructive: true, onSelect: () => setRemoving(member) });
    return items;
  };

  /* ---------------------------------------------------------------- add */
  const [adding, setAdding] = useState(false);
  const [term, setTerm] = useState("");
  const [picked, setPicked] = useState<DirectoryPersonRow[]>([]);
  const [shareHistory, setShareHistory] = useState(false);
  const people = useQuery(chatPeopleQuery(term, { enabled: canManage && adding }));
  const memberIds = new Set(conversation?.allMembers.map((member) => member.userId) ?? []);
  const candidates = (people.data?.rows ?? []).filter((person) => !memberIds.has(person.userId));
  const isPicked = (userId: string) => picked.some((person) => person.userId === userId);
  const add = useMutation({
    mutationFn: (input: { id: string; userIds: string[]; shareHistory: boolean }) =>
      chatApi.addMembers(input.id, input.userIds, input.shareHistory),
    onSuccess: async (_result, input) => {
      setPicked([]);
      setTerm("");
      setAdding(false);
      setShareHistory(false);
      await invalidateChat(queryClient);
      toast.success(`${formatCount(input.userIds.length)} kişi gruba eklendi.`);
    },
    onError: (error) => toast.errorMessage("Üye eklenemedi", chatErrorMessage(error)),
  });

  const title = conversation ? (conversationTitle(conversation, meId) ?? "Grup") : "Grup";

  return (
    <>
      <Drawer
        open={conversationId !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={title}
        subtitle={conversation ? `${formatCount(conversation.memberCount)} üye` : undefined}
        reserveKey="sohbet.grup-uyeleri"
        loading={detail.isPending && conversationId !== null}
        skeleton={<MembersSkeleton />}
        footer={
          conversation ? (
            <Button variant="secondary" onClick={() => onRequestLeave(conversation)}>
              Gruptan ayrıl
            </Button>
          ) : undefined
        }
      >
        {detail.error ? (
          <EmptyState
            variant="error-loading"
            size="sm"
            title="Grup bilgileri yüklenemedi"
            description={chatErrorMessage(detail.error)}
            action={
              <Button size="sm" variant="secondary" onClick={() => void detail.refetch()}>
                Yeniden dene
              </Button>
            }
          />
        ) : conversation ? (
          <div className="flex flex-col gap-6">
            {canManage ? (
              <section className="flex flex-col gap-2">
                <TextField
                  label="Grup adı"
                  required
                  maxLength={160}
                  value={titleValue}
                  onChange={(event) => setTitleDraft({ id: conversation.id, value: event.target.value })}
                />
                <div>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={rename.isPending}
                    disabled={!titleChanged}
                    {...(titleChanged ? {} : { disabledReason: "Yeni bir ad yazın" })}
                    onClick={() =>
                      rename.mutate({ id: conversation.id, title: trimmedTitle, version: conversation.version })
                    }
                  >
                    Adı kaydet
                  </Button>
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-fg">Üyeler</h3>
                {canManage && !adding ? (
                  <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                    Üye ekle
                  </Button>
                ) : null}
              </div>
              {canManage && owners.length === 1 && owners[0]?.userId === meId && conversation.memberCount > 1 ? (
                <p className="text-xs text-fg-muted">
                  Grubun tek sahibi sizsiniz. Ayrılmadan önce bir üyeyi sahip yapın; bir grubun birden çok sahibi olabilir.
                </p>
              ) : null}
              <ul className="flex flex-col" aria-label="Grup üyeleri">
                {conversation.allMembers.map((member) => {
                  const src = personPhotoUrl(member.userId, member.hasPhoto, member.photoFileId);
                  const actions = memberActions(member);
                  return (
                    <li key={member.userId} className="flex items-center gap-3 py-2">
                      <Avatar name={member.displayName} size="sm" {...(src ? { src } : {})} />
                      <span className="min-w-0 flex-1 truncate text-sm text-fg">{displayNameOf(member, meId)}</span>
                      {member.role === "OWNER" ? <Badge tone="info">Sahip</Badge> : null}
                      {actions.length > 0 ? (
                        <Menu actions={actions} label={`${member.displayName} için işlemler`} />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>

            {canManage && adding ? (
              <section className="flex flex-col gap-2">
                <SearchInput value={term} onChange={setTerm} label="Kişi ara" placeholder="Ad, kullanıcı adı veya e-posta" />
                {people.error ? (
                  <Banner tone="danger" title="Kişiler yüklenemedi">
                    {chatErrorMessage(people.error)}
                  </Banner>
                ) : null}
                {people.data && candidates.length === 0 && !people.error ? (
                  <EmptyState
                    variant={term ? "no-results" : "no-data"}
                    size="sm"
                    title={term ? "Kişi bulunamadı" : "Eklenecek kişi yok"}
                    description={
                      term
                        ? "Başka bir ad, kullanıcı adı ya da e-posta deneyin."
                        : "Rehberde gösterilen herkes zaten bu grupta."
                    }
                  />
                ) : (
                  <ul className="flex max-h-64 flex-col overflow-y-auto" aria-label="Eklenecek kişiler">
                    {candidates.map((person) => {
                      const src = personPhotoUrl(person.userId, person.hasPhoto, person.photoFileId);
                      return (
                        <li key={person.userId}>
                          <button
                            type="button"
                            aria-pressed={isPicked(person.userId)}
                            onClick={() =>
                              setPicked((current) =>
                                isPicked(person.userId)
                                  ? current.filter((candidate) => candidate.userId !== person.userId)
                                  : [...current, person],
                              )
                            }
                            className="flex w-full items-center gap-3 px-1 py-2 text-start active:bg-surface-2"
                          >
                            <Avatar name={person.displayName} size="sm" {...(src ? { src } : {})} />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-fg">{person.displayName}</span>
                              {personSubtitle(person) ? (
                                <span className="block truncate text-xs text-fg-muted">{personSubtitle(person)}</span>
                              ) : null}
                            </span>
                            {isPicked(person.userId) ? (
                              <Check size={16} strokeWidth={2} className="shrink-0 text-brand" aria-hidden />
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <SwitchField
                  label="Geçmişi paylaş"
                  checked={shareHistory}
                  onChange={setShareHistory}
                  description="Açıkken eklenen kişiler gruptaki önceki mesajları da görür. Kapalıyken yalnız eklendikleri andan sonrasını görürler."
                />
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="quiet"
                    disabled={add.isPending}
                    onClick={() => {
                      setAdding(false);
                      setPicked([]);
                      setTerm("");
                    }}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    size="sm"
                    loading={add.isPending}
                    disabled={picked.length === 0}
                    {...(picked.length === 0 ? { disabledReason: "En az bir kişi seçin" } : {})}
                    onClick={() =>
                      add.mutate({ id: conversation.id, userIds: picked.map((person) => person.userId), shareHistory })
                    }
                  >
                    {picked.length > 0 ? `${formatCount(picked.length)} kişiyi ekle` : "Ekle"}
                  </Button>
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setRemoving(null);
        }}
        title="Üyeyi gruptan çıkarıyor musunuz?"
        impact={
          removing
            ? `«${removing.displayName}» gruptaki mesajları artık göremez. Geri eklemek için yeniden üye eklemeniz gerekir.`
            : ""
        }
        confirmLabel="Gruptan çıkar"
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          if (removing && conversation) remove.mutate({ id: conversation.id, userId: removing.userId });
        }}
      />
    </>
  );
}

/** Three member rows' worth of grey, the height the list usually has. */
function MembersSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-3">
      <SkLine w="40%" />
      <SkLine w="70%" />
      <SkLine w="60%" />
      <SkLine w="65%" />
    </div>
  );
}

/**
 * SON SAHİP AYRILMAK İSTİYOR (H7). Eskiden «Gruptan ayrılınamadı — İşlem tamamlanamadı» (422
 * `LAST_OWNER`) diyordu ve yolu yoktu. Şimdi ayrılmadan önce yeni sahibi seçtiriyor: seçilen üye
 * sahip yapılıyor (`POST …/members/:userId/role`), sonra kişi ayrılıyor. İkinci adım düşerse yeni
 * sahip sahip kalıyor — grup yine yönetilebilir, yalnız ayrılma yeniden denenmeli.
 */
export function HandOverDialog({
  conversation,
  meId,
  onClose,
  onLeft,
}: {
  conversation: ChatConversationDetail | null;
  meId: string;
  onClose: () => void;
  onLeft: (conversationId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [successor, setSuccessor] = useState<string | null>(null);
  const others = conversation?.allMembers.filter((member) => member.userId !== meId) ?? [];
  const handOver = useMutation({
    mutationFn: async (input: { id: string; successor: string }) => {
      await chatApi.setMemberRole(input.id, input.successor, "OWNER");
      await chatApi.removeMember(input.id, meId);
    },
    onSuccess: async (_result, input) => {
      setSuccessor(null);
      onClose();
      onLeft(input.id);
      await invalidateAfterLeaving(queryClient);
      toast.success("Sahipliği devrettiniz ve gruptan ayrıldınız.");
    },
    onError: async (error) => {
      await invalidateChat(queryClient);
      toast.errorMessage("Gruptan ayrılınamadı", chatErrorMessage(error));
    },
  });

  return (
    <Modal
      open={conversation !== null}
      onOpenChange={(open) => {
        if (!open && !handOver.isPending) {
          setSuccessor(null);
          onClose();
        }
      }}
      title="Önce sahipliği devredin"
      description="Grubun tek sahibi sizsiniz. Grubu yönetecek bir üye seçin: o kişi grup sahibi olur, siz gruptan ayrılırsınız."
      isPending={handOver.isPending}
      footer={
        <>
          <Button variant="secondary" disabled={handOver.isPending} onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            variant="destructive"
            loading={handOver.isPending}
            disabled={successor === null}
            {...(successor === null ? { disabledReason: "Yeni sahibi seçin" } : {})}
            onClick={() => {
              if (conversation && successor) handOver.mutate({ id: conversation.id, successor });
            }}
          >
            Sahip yap ve ayrıl
          </Button>
        </>
      }
    >
      <SelectField
        label="Yeni sahip"
        required
        value={successor}
        onChange={setSuccessor}
        placeholder="Bir üye seçin"
        options={others.map((member) => ({ value: member.userId, label: member.displayName }))}
      />
    </Modal>
  );
}
